import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
} from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
	theme: Theme;
	setTheme: (theme: Theme) => void;
	isDark: boolean;
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const STORAGE_KEY = "theme-preference";

// =====================
// Hook
// =====================

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<Theme>(() => {
		// Check localStorage first
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === "light" || stored === "dark" || stored === "system") {
			return stored;
		}
		return "system";
	});

	const [isDark, setIsDark] = useState(false);

	// Calculate if dark mode should be active
	const calculateIsDark = useCallback((currentTheme: Theme): boolean => {
		if (currentTheme === "dark") return true;
		if (currentTheme === "light") return false;

		// System preference
		if (typeof window !== "undefined") {
			return window.matchMedia("(prefers-color-scheme: dark)").matches;
		}
		return false;
	}, []);

	// Update isDark when theme or system preference changes
	useEffect(() => {
		setIsDark(calculateIsDark(theme));
	}, [theme, calculateIsDark]);

	// Apply theme to document
	useEffect(() => {
		const root = document.documentElement;

		if (isDark) {
			root.classList.add("dark");
		} else {
			root.classList.remove("dark");
		}
	}, [isDark]);

	// Listen for system theme changes
	useEffect(() => {
		if (theme !== "system") return;

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

		const handleChange = () => {
			setIsDark(calculateIsDark("system"));
		};

		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [theme, calculateIsDark]);

	// Set theme and persist
	const setTheme = useCallback((newTheme: Theme) => {
		setThemeState(newTheme);
		localStorage.setItem(STORAGE_KEY, newTheme);
	}, []);

	// Toggle between light and dark
	const toggleTheme = useCallback(() => {
		setTheme(isDark ? "light" : "dark");
	}, [isDark, setTheme]);

	return (
		<ThemeContext.Provider value={{ theme, setTheme, isDark, toggleTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}

// =====================
// Hook
// =====================

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}

// =====================
// Utility Components
// =====================

interface ThemeScriptProps {
	forceTheme?: Theme;
}

export function ThemeScript({ forceTheme }: ThemeScriptProps) {
	const theme = forceTheme || "system";

	return (
		<script
			dangerouslySetInnerHTML={{
				__html: `
					(function() {
						var theme = localStorage.getItem('${STORAGE_KEY}') || '${theme}';
						var isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
						if (isDark) {
							document.documentElement.classList.add('dark');
						}
					})();
				`,
			}}
		/>
	);
}
